import { ActivityType, EventType } from 'types/models';
import { PageContentWrapper, PageSubtitle, PageTitle, } from 'custom-style-components';
import React, { useEffect, useRef, useState } from 'react';

import ActivityCard from 'components/ActivityCard';
import { CancelTokenSource } from 'axios';
import ErrorMessage from 'components/ErrorMessage';
import { GenericFrontError } from 'errors/GenericFrontError';
import GoBackButton from 'components/GoBackButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { ResponseError } from 'errors/ResponseError';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyActivitiesByEvent } from 'services/fetch/activities';
import { fetchOneEvent } from 'services/fetch/events';
import renderEventName from 'utils/renderEventName';
import { useAuth } from 'hooks/auth';
import { useParams } from 'react-router';

interface ParamTypes {
    eventId: string;
}

const EventMyActivitiesPage: React.FC = () => {
    const [userActivities, setUserActivities] = useState<ActivityType[]>([]);
    const [activities, setActivities] = useState<ActivityType[]>();
    const [loading, setLoading] = useState(true);
    const [fetchActivitiesError, setFetchActivitiesError] = useState(false);
    const [moreActivities, setMoreActivities] = useState(false);
    const [event, setEvent] = useState<EventType>();
    const [error, setError] = useState<string>();

    const { eventId } = useParams<ParamTypes>();

    const { user } = useAuth();
    const cancelTokenRef = useRef<CancelTokenSource | null>(null);

    const loadActivities = (silent = false) => {
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel('Operation canceled due to new request.');
        }
        cancelTokenRef.current = createCancelTokenSource();
        const source = cancelTokenRef.current;

        if (event && user) {
            if (!event?.statusActive) {
                setLoading(false);
                setMoreActivities(true);
                return;
            }
            setMoreActivities(false);
            if (!silent) setLoading(true);
            fetchManyActivitiesByEvent(source.token, event.id.toString(), {
                limit: Number.MAX_SAFE_INTEGER,
                fromUser: user.id,
            })
                .then(({ activities }) => {
                    setUserActivities(activities);
                    setActivities(activities);
                })
                .catch((err) => {
                    if (err.message !== 'Operation canceled due to new request.') {
                        setFetchActivitiesError(err.message);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        const source = createCancelTokenSource();
        setLoading(true);
        fetchOneEvent(source.token, eventId)
            .then((event) => {
                setEvent(event);
            })
            .catch((error) => {
                if (error instanceof GenericFrontError) {
                    let message = error.message;
                    if (error instanceof ResponseError && error.status === 404)
                        message = 'Evento não encontrado.';
                    setError(message);
                }
            });
        return () => {
            source.cancel();
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Component unmounted.');
            }
        };
    }, []);

    useEffect(() => {
        loadActivities();
    }, [event]);

    const renderActivities = (activities: ActivityType[] | undefined) => {
        if (event && activities && activities.length > 0) {
            const actReturn = activities.map((activity) => {
                let isRegistered = false;
                if (user) {
                    isRegistered = !!userActivities.find(
                        (userActivity) => userActivity.id === activity.id
                    );
                }
                return (
                    <ActivityCard
                        key={activity.id}
                        event={event}
                        activity={activity}
                        alreadyRegistered={isRegistered}
                        refreshActivities={() => loadActivities(true)}
                    />
                );
            });

            return actReturn;
        }

        return <p>Você não se matriculou em nenhuma atividade deste evento.</p>;
    };

    return (
        <>
            <PageContentWrapper>
                <GoBackButton />
                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : fetchActivitiesError ? (
                    <ErrorMessage>{fetchActivitiesError}</ErrorMessage>
                ) : loading ? (
                    <LoadingSpinner />
                ) : moreActivities ? (
                    <PageTitle>Em breve!</PageTitle>
                ) : (
                    <>
                        <PageTitle>Minhas atividades</PageTitle>
                        <PageSubtitle>
                            {event && renderEventName(event)}
                        </PageSubtitle>

                        {renderActivities(activities)}
                    </>
                )}
            </PageContentWrapper>
        </>
    );
};

export default EventMyActivitiesPage;
